import { useMemo } from "react";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import type { ReservationWrapper } from "@b2bportal/core-types";
import type { Itinerary as CarItinerary } from "@b2bportal/ground-api";
import type { HomesReservation } from "@b2bportal/homes-api";
import type { HotelItinerarySummary } from "@b2bportal/lodging-api";
import { Product } from "@b2bportal/purchase-api";
import type { ActivityReservation } from "@b2bportal/rbc-activities-api";
import type { PackageItinerarySummary } from "@b2bportal/rbc-bundled-packages-api";
import {
  categorizeHtsAssistLobbyTrips,
  type HtsAssistLobbyTrip,
} from "./htsAssistLobbyTrips";
import { useTripsList } from "@/overrides/trips/hooks/useTripsList";
import { useRbcTripProducts } from "@/pages/Trips/useRbcTripProducts";

const asSupportedLobbyTrips = (
  trips: ReservationWrapper<unknown>[]
): HtsAssistLobbyTrip[] => {
  const supportedTrips: HtsAssistLobbyTrip[] = [];

  trips.forEach((trip) => {
    switch (trip.productType) {
      case Product.Package:
        supportedTrips.push({
          ...trip,
          productType: Product.Package,
          reservation: trip.reservation as PackageItinerarySummary,
        });
        break;
      case Product.Homes:
        supportedTrips.push({
          ...trip,
          productType: Product.Homes,
          reservation: trip.reservation as HomesReservation,
        });
        break;
      case Product.Activities:
        supportedTrips.push({
          ...trip,
          productType: Product.Activities,
          reservation: trip.reservation as ActivityReservation,
        });
        break;
      case Product.Flight:
        supportedTrips.push({
          ...trip,
          productType: Product.Flight,
          reservation:
            trip.reservation as BookedFlightItineraryWithDepartureTime,
        });
        break;
      case Product.Hotel:
        supportedTrips.push({
          ...trip,
          productType: Product.Hotel,
          reservation: trip.reservation as HotelItinerarySummary,
        });
        break;
      case Product.Ground:
        supportedTrips.push({
          ...trip,
          productType: Product.Ground,
          reservation: trip.reservation as CarItinerary,
        });
        break;
    }
  });

  return supportedTrips;
};

export const useHtsAssistLobbyTrips = () => {
  const { allProducts } = useRbcTripProducts();
  const { context } = useTripsList({ products: allProducts });

  return {
    categories: useMemo(
      () => categorizeHtsAssistLobbyTrips(asSupportedLobbyTrips(context.trips)),
      [context.trips]
    ),
    hasError: context.hasError,
    isLoading: context.isLoading,
    retry: () =>
      Object.values(allProducts).forEach((product) => product.fetch()),
  };
};
